import React, { useCallback, useEffect, useState, useMemo, useContext } from 'react';
import classNames from 'classnames';
import { formatInTimeZone } from 'date-fns-tz';
import { Box, Grid, Typography } from '@mui/material';
import { trackEvent } from '@surfline/web-common';

import { PRODUCT_TUTORIALS } from 'common/constants';
import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import ForecastGraphAccordion from 'components/ForecastGraphAccordion';
import { RewindsContext } from 'contexts/RewindsContext';
import useAccordionToggle from 'hooks/useAccordionToggle';
import type { CamPlayerGridState } from 'hooks/useCamPlayerGrid';
import { useMaxWidthTabletLarge } from 'hooks/useMediaQueries';
import { getSpotReportAssociated, getSpotDetails } from 'selectors/spot';
import { useUserPermissionStatus, useUserUnits } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';
import type { Camera } from 'types/camera';
import type { SpotDetails } from 'types/spot';
import { floorToMidnightForTZ, oneHour, oneMinute, oneSecond } from 'utils/time';

import {
  CameraInsightsCarousel,
  type CameraInsightsCarouselProps,
} from './components/CameraInsightsCarousel';
import OutOfRangeButton from './components/OutOfRangeButton';
import useCameraInsights from './hooks/useCameraInsights';
import getDayName from './utils/getDayName';

import styles from './CameraInsights.module.scss';

interface CameraInsightsContentProps
  extends Pick<CameraInsightsCarouselProps, 'scrollEventAnalyticsValues'> {
  camera: Camera;
  endDate?: number;
  nowDate: Date;
  spot: SpotDetails;
  startDate?: number;
  timezone: string;
}

const TEN_MINUTES_IN_MS = 60 * 10000;

const CameraInsightsContent: React.FC<CameraInsightsContentProps> = ({
  camera,
  endDate,
  nowDate,
  scrollEventAnalyticsValues,
  spot,
  startDate,
  timezone,
}) => {
  const units = useUserUnits();
  const [clickedSummary, setClickedSummary] = useState<boolean>(false);
  const [crowdStartDate, setCrowdStartDate] = useState<string | undefined>();

  const isMorning = useMemo(() => {
    if (!startDate) return false;
    const formattedTime = formatInTimeZone(startDate, timezone, 'HH');
    const startHour = parseInt(formattedTime, 10);
    return startHour < 12;
  }, [startDate, timezone]);

  const { insights, crowd } = useCameraInsights({
    camera,
    crowdStartDate,
    endDate,
    startDate,
    units,
    spot,
  });

  const { data: insightsData } = insights;

  const isTooDark = insightsData?.current?.status?.tooDark ?? false;

  const isBeforeToday = useMemo(
    () => endDate && endDate < floorToMidnightForTZ(nowDate.getTime(), timezone),
    [endDate, nowDate, timezone],
  );

  const selectedDay = useMemo(
    () =>
      insightsData?.lastFullDay?.endTimestamp
        ? getDayName(new Date(insightsData.lastFullDay.endTimestamp * oneSecond), nowDate, timezone)
        : undefined,
    [insightsData, nowDate, timezone],
  );

  const outOfBoundsOnClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const target = event.currentTarget;
      const isAnimating = target
        ?.getAnimations?.()
        .some((animation) => animation.playState === 'running');
      if (isAnimating) return;
      const currentHeight = target.clientHeight;
      const animation = target?.animate?.([{ height: currentHeight }, { height: '194px' }], {
        duration: 500,
        easing: 'ease-in-out',
      });
      if (animation) {
        animation.onfinish = () => {
          animation.onfinish = null;
          setClickedSummary(true);
        };
      } else {
        setClickedSummary(true);
      }
      trackEvent('Button Clicked', {
        buttonText: "View previous day's insights",
        pageName: 'Spot Report',
        uiElement: 'Smart Cam insights',
      });
    },
    [],
  );

  useEffect(() => {
    setClickedSummary(false);
    if (isTooDark && isMorning && startDate) {
      // set to yesterdays date from startDate in this format 2024-01-01
      const yesterday = new Date(startDate);
      yesterday.setDate(yesterday.getDate() - 1);
      setCrowdStartDate(formatInTimeZone(yesterday, timezone, 'yyyy-MM-dd'));
    } else {
      const todaysDate = formatInTimeZone(
        floorToMidnightForTZ(nowDate.getTime(), timezone),
        timezone,
        'yyyy-MM-dd',
      );
      const newCrowdStartDate = startDate
        ? formatInTimeZone(startDate, timezone, 'yyyy-MM-dd')
        : undefined;
      setCrowdStartDate(newCrowdStartDate !== todaysDate ? newCrowdStartDate : undefined);
    }
  }, [isMorning, isTooDark, nowDate, startDate, timezone]);

  return (
    <Box className={styles.content} data-testid="camera-insights">
      {isTooDark && isMorning && insightsData?.lastFullDay?.endTimestamp && !clickedSummary ? (
        <OutOfRangeButton day={selectedDay} onClick={outOfBoundsOnClick} />
      ) : (
        <CameraInsightsCarousel
          crowd={crowd}
          endDate={endDate}
          insights={insights}
          isBeforeToday={!!isBeforeToday}
          isTooDark={isTooDark}
          scrollEventAnalyticsValues={scrollEventAnalyticsValues}
          timezone={timezone}
        />
      )}
    </Box>
  );
};

const AccordionLabelCameraInsights = (
  <Typography variant="h2" component="span" color="white" className={styles.accordionLabel}>
    Smart Cam Insights
  </Typography>
);

const accordionInfoProps = {
  articleId: PRODUCT_TUTORIALS.articles.cameraInsights.id,
  className: styles.accordionInfo,
  segmentProps: {
    modalType: 'tutorial',
    uiElement: 'cameraInsights',
  },
  tutorialId: PRODUCT_TUTORIALS.tutorials.smartCams.id,
  tooltip: PRODUCT_TUTORIALS.articles.cameraInsights.tooltip,
};

interface CameraInsightsAccordionProps extends Pick<CameraInsightsContentProps, 'camera'> {
  camPlayerGridState: CamPlayerGridState;
}

const CameraInsightsAccordion: React.FC<CameraInsightsAccordionProps> = ({
  camera,
  camPlayerGridState,
}) => {
  const { isAccordionOpen, toggleAccordion } = useAccordionToggle({
    id: 'cameraInsights',
  });
  const { currentPlaylist } = useContext(RewindsContext);
  const { timezone } = useAppSelector(getSpotReportAssociated);
  const spot = useAppSelector(getSpotDetails);
  const isTabletLgMax = useMaxWidthTabletLarge();

  const analyticsTrackingBaseValues = useMemo(
    () => ({
      cameraId: camera?._id,
      cameraName: camera?.title,
      premiumCam: camera?.isPremium,
      spotId: spot._id,
      spotName: spot.name,
      subregionId: spot.subregion?._id,
    }),
    [camera?._id, camera?.isPremium, camera?.title, spot._id, spot.name, spot.subregion?._id],
  );

  const customAccordionAnalyticsValues = useMemo(
    () => ({
      title: 'Clicked Cam Insights',
      properties: {
        ...analyticsTrackingBaseValues,
        menuExpanded: !isAccordionOpen,
      },
    }),
    [analyticsTrackingBaseValues, isAccordionOpen],
  );

  const nowDate = useMemo(() => new Date(), []);

  const endDate = useMemo(() => {
    if (currentPlaylist?.bucketEnd) {
      const diffInMinutes = (nowDate.getTime() - currentPlaylist.bucketEnd.getTime()) / oneMinute;
      if (diffInMinutes > 15) return Number(currentPlaylist.bucketEnd);
    }
    return nowDate.getTime();
  }, [currentPlaylist, nowDate]);

  const startDate = useMemo(() => {
    if (currentPlaylist?.bucketEnd) {
      const diffInMinutes = (nowDate.getTime() - currentPlaylist.bucketEnd.getTime()) / oneMinute;
      if (diffInMinutes > 15) {
        return endDate ? endDate - oneHour : undefined;
      }
    }
    /*
    Note: When the bucket end date is not set or is within the last 15 miuntes, we're fetching "current" data.
    In this case, we want to make the range 70 minutes (instead of 60 minutes) to  account for the 10
    minute delay with the ML processing the rewind video file to increase the likelyhood of getting
    60 minutes of data. The API will provide the same response as if we didn't set a start or endDate in
    the query params.
    */
    return endDate ? endDate - oneHour - TEN_MINUTES_IN_MS : undefined;
  }, [currentPlaylist, endDate, nowDate]);

  return (
    <Grid
      container
      columns={{
        mobile: camPlayerGridState.container.mobile,
        tabletLg: camPlayerGridState.container.tabletLg,
        desktop: camPlayerGridState.container.desktop,
        desktopMd: camPlayerGridState.container.desktopMd,
        desktopLg: camPlayerGridState.container.desktopLg,
      }}
      columnSpacing={2}
      justifyContent="space-between"
      className={classNames({
        [styles.grid]: true,
        [styles.gridWithAngles]: camPlayerGridState.props.hasAngles,
        [styles.gridWithCompanionAd]: camPlayerGridState.props.shouldRenderCompanionAd,
        [styles.gridWithMultiCam]: camPlayerGridState.props.multiCamViewActive,
        [styles.gridWithMultiCam4]:
          camPlayerGridState.props.multiCamViewActive &&
          camPlayerGridState.props.camerasLength === 4,
      })}
    >
      <Grid
        item
        className={styles.gridLeftPadding}
        tabletLg={camPlayerGridState.items[0].tabletLg}
        desktop={camPlayerGridState.items[0].desktop}
        desktopMd={camPlayerGridState.items[0].desktopMd}
        desktopLg={camPlayerGridState.items[0].desktopLg}
      />
      <Grid
        item
        mobile={camPlayerGridState.items[1].mobile}
        tabletLg={camPlayerGridState.items[1].tabletLg}
        desktop={camPlayerGridState.items[1].desktop}
        desktopMd={camPlayerGridState.items[1].desktopMd}
        desktopLg={camPlayerGridState.items[1].desktopLg}
      >
        <ForecastGraphAccordion
          className={styles.wrapper}
          customTrackingValues={customAccordionAnalyticsValues}
          disableFullBleed={!isTabletLgMax}
          id="cameraInsights"
          information={accordionInfoProps}
          isOpen={isAccordionOpen}
          label={AccordionLabelCameraInsights}
          onChange={toggleAccordion}
        >
          <Box
            className={classNames({
              'sl-section-container': isTabletLgMax,
            })}
          >
            <CameraInsightsContent
              camera={camera}
              endDate={endDate}
              nowDate={nowDate}
              scrollEventAnalyticsValues={analyticsTrackingBaseValues}
              spot={spot}
              startDate={startDate}
              timezone={timezone}
            />
          </Box>
        </ForecastGraphAccordion>
      </Grid>
      {!camPlayerGridState.props.multiCamViewActive ? (
        <Grid
          item
          className={styles.gridRightPadding}
          mobile={camPlayerGridState.items[2].mobile}
          tabletLg={camPlayerGridState.items[2].tabletLg}
          desktop={camPlayerGridState.items[2].desktop}
          desktopMd={camPlayerGridState.items[2].desktopMd}
          desktopLg={camPlayerGridState.items[2].desktopLg}
        />
      ) : (
        <Grid
          item
          className={styles.gridRightPadding}
          tabletLg={camPlayerGridState.items[3].tabletLg}
          desktop={camPlayerGridState.items[3].desktop}
          desktopMd={camPlayerGridState.items[3].desktopMd}
          desktopLg={camPlayerGridState.items[3].desktopLg}
        />
      )}
    </Grid>
  );
};

interface CameraInsightsWrapperProps extends CameraInsightsAccordionProps {
  camPlayerGridState: CamPlayerGridState;
}

const CameraInsightsWrapper: React.FC<CameraInsightsWrapperProps> = ({
  camera,
  camPlayerGridState,
}) => {
  const { hasCamInsightPermissions } = useUserPermissionStatus();

  return hasCamInsightPermissions ? (
    <CameraInsightsAccordion camera={camera} camPlayerGridState={camPlayerGridState} />
  ) : (
    <Box data-testid="camera-insights-hidden" className={styles.hiddenCamInsights} />
  );
};

interface CameraInsightsProps extends CameraInsightsAccordionProps {
  camPlayerGridState: CamPlayerGridState;
  isError?: boolean;
}

const CameraInsights: React.FC<CameraInsightsProps> = ({ camera, camPlayerGridState, isError }) => (
  <ErrorBoundary
    error={isError}
    errorBoundaryId="camera-insights"
    render={() => (
      <Box className={styles.wrapper}>
        <Box className={styles.wrapperError} data-testid="camera-insights-error">
          <ErrorMessage className={styles.wrapperErrorMessage} />
        </Box>
      </Box>
    )}
  >
    <CameraInsightsWrapper camera={camera} camPlayerGridState={camPlayerGridState} />
  </ErrorBoundary>
);

export default CameraInsights;
